/**
 * Utility functions for admin logic and formatting used across the app
 */

export const formatDateTime = (timestamp: number): string => {
  return new Date(timestamp).toLocaleString("en-GB", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
};

export const formatPercentage = (decimal: number): string => {
  return `${(decimal * 100).toFixed(2)}%`;
};

export const formatPennies = (pennies: number): string => {
  return `£${(pennies / 100).toFixed(2)}`;
};

export const formatCurrency = (amount: number, currency: string = "£"): string => {
  return `${currency}${amount.toLocaleString()}`;
};

export const parsePercentage = (percentage: string): number => {
  return parseFloat(percentage.replace('%', '')) / 100;
};

export const parseCurrency = (currency: string): number => {
  return parseFloat(currency.replace(/[£$€,\s]/g, '')) || 0;
};

/**
 * Returns the primary email for the WorkOS AuthKit user object, if available.
 */
export const getUserEmail = (user: any): string | null => {
  if (!user) return null;
  // Common shape: user.email
  if (typeof user.email === "string" && user.email.length > 0) return user.email;
  // Fallbacks some SDKs expose
  if (Array.isArray(user.emails) && user.emails.length > 0) return user.emails[0];
  if (Array.isArray(user.emailAddresses) && user.emailAddresses.length > 0) {
    const primary = user.emailAddresses.find((e: any) => e.primary) || user.emailAddresses[0];
    return typeof primary === "string" ? primary : primary?.email ?? null;
  }
  return null;
};

/**
 * Returns the ID for the WorkOS AuthKit user object, if available.
 */
export const getUserId = (user: any): string | null => {
  if (!user) return null;
  if (typeof user.id === "string" && user.id.length > 0) return user.id;
  if (typeof user.userId === "string" && user.userId.length > 0) return user.userId;
  return null;
};

/**
 * Attempt to extract role names from various shapes used by providers.
 */
export const getUserRoles = (user: any): Array<string> => {
  const roles: Array<string> = [];
  if (!user) return roles;
  // Direct role string
  if (typeof user.role === "string") roles.push(user.role);
  // roles: string[]
  if (Array.isArray(user.roles)) {
    for (const r of user.roles) {
      if (typeof r === "string") roles.push(r);
      else if (r && typeof r.slug === "string") roles.push(r.slug);
      else if (r && typeof r.name === "string") roles.push(r.name);
    }
  }
  // organizationMemberships: [{ role: string | { slug/name } }]
  if (Array.isArray((user as any).organizationMemberships)) {
    for (const m of (user as any).organizationMemberships) {
      const r = m?.role;
      if (typeof r === "string") roles.push(r);
      else if (r && typeof r.slug === "string") roles.push(r.slug);
      else if (r && typeof r.name === "string") roles.push(r.name);
    }
  }
  // authorization?.roles: string[]
  if (user.authorization && Array.isArray(user.authorization.roles)) {
    for (const r of user.authorization.roles) {
      if (typeof r === "string") roles.push(r);
    }
  }
  return roles
    .filter(Boolean)
    .map((r) => String(r).trim().toLowerCase());
};

/**
 * Get user permissions from the user object or JWT token.
 */
export const getUserPermissions = (user: any): Array<string> => {
  if (!user) return [];
  
  // Check user object first
  if (Array.isArray(user.permissions)) {
    return user.permissions.map((p: any) => String(p).trim().toLowerCase());
  }
  
  // Check if permissions are in a nested object
  if (user.authorization && Array.isArray(user.authorization.permissions)) {
    return user.authorization.permissions.map((p: any) => String(p).trim().toLowerCase());
  }
  
  return [];
};

/**
 * Check if a user has a specific permission.
 */
export const hasPermission = (user: any, permission: string): boolean => {
  const permissions = getUserPermissions(user);
  return permissions.includes(permission.toLowerCase());
};

/**
 * Determine if a WorkOS AuthKit user has admin privileges based on roles.
 * We check common role sources and consider "admin" a privileged role.
 */
export const isAdminUser = (user: any): boolean => {
  const roles = getUserRoles(user);
  return roles.includes("admin");
};

/**
 * Check if user can view saved results based on loan-calc:saved-results:read permission.
 */
export const canViewSavedResults = (user: any): boolean => {
  return hasPermission(user, "loan-calc:saved-results:read");
};

/**
 * Check if user can write/update variable configurations based on loan-calc:variables:write permission.
 */
export const canWriteVariables = (user: any): boolean => {
  return hasPermission(user, "loan-calc:variables:write");
};

/**
 * Extract roles and permissions from JWT payload.
 */
export const extractRolesAndPermissionsFromJWT = (jwtPayload: any) => {
  return {
    roles: jwtPayload?.roles || [],
    permissions: jwtPayload?.permissions || []
  };
};

/**
 * Check if user can access admin panel based on loan-calc:variables:read permission.
 */
export const canAccessAdmin = (user: any): boolean => {
  return hasPermission(user, "loan-calc:variables:read");
};
